import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { EMOJI_CATEGORIES, getAllEmojis } from "./emojiData";
import EmojiPickerButton from "./EmojiPickerButton";
import {
  getEmojiPickerPosition,
  type EmojiPickerPosition,
} from "./getEmojiPickerPosition";

interface EmojiPickerPopoverProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  pickerRef?: (node: HTMLDivElement | null) => void;
}

function EmojiPickerPopover({
  onSelect,
  onClose,
  anchorEl,
  pickerRef,
}: EmojiPickerPopoverProps) {
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].id);
  const [searchQuery, setSearchQuery] = useState("");
  const [position, setPosition] = useState(null as EmojiPickerPosition | null);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredEmojis = useMemo((): string[] => {
    if (!normalizedQuery) {
      const category = EMOJI_CATEGORIES.find(
        (item) => item.id === activeCategory,
      );
      return [...(category?.emojis ?? EMOJI_CATEGORIES[0].emojis)];
    }

    return getAllEmojis()
      .filter((item) => {
        const categoryMatch = item.categoryLabel
          .toLowerCase()
          .includes(normalizedQuery);
        const keywordMatch = item.keywords.some((keyword) =>
          keyword.includes(normalizedQuery),
        );
        return categoryMatch || keywordMatch;
      })
      .map((item) => item.emoji);
  }, [activeCategory, normalizedQuery]);

  useEffect(() => {
    if (!anchorEl) {
      return;
    }

    const updatePosition = () => {
      setPosition(getEmojiPickerPosition(anchorEl));
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!anchorEl || !position) {
    return null;
  }

  const gridMaxHeight = Math.max(position.maxHeight - 160, 180);

  return createPortal(
    <div
      ref={pickerRef}
      className="fixed flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: position.maxHeight,
        zIndex: 10050,
      }}
      role="dialog"
      aria-label="Emoji picker"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="shrink-0 border-b border-gray-100 bg-white p-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search emojis..."
          autoFocus
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-instant-teams-blue-Main focus:ring-1 focus:ring-instant-teams-blue-Main"
          aria-label="Search emojis"
        />
      </div>

      {!normalizedQuery && (
        <div className="shrink-0 flex flex-wrap gap-1 border-b border-gray-100 bg-white p-2">
          {EMOJI_CATEGORIES.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                activeCategory === category.id
                  ? "bg-instant-teams-blue-Main text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.label}
            </button>
          ))}
        </div>
      )}

      {normalizedQuery && (
        <div className="shrink-0 border-b border-gray-100 bg-white px-3 py-2 text-xs text-gray-500">
          {filteredEmojis.length} result
          {filteredEmojis.length === 1 ? "" : "s"}
        </div>
      )}

      <div
        className="min-h-0 flex-1 overflow-y-auto bg-white p-2"
        style={{ maxHeight: gridMaxHeight }}
      >
        {filteredEmojis.length > 0 ? (
          <div className="grid grid-cols-8 gap-1">
            {filteredEmojis.map((emoji: string, index: number) => (
              <EmojiPickerButton
                key={`${emoji}-${index}`}
                emoji={emoji}
                onSelect={onSelect}
              />
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500">
            No emojis found
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default EmojiPickerPopover;
